import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../shared/api/api-context.js";
import { requireData } from "../../shared/api/api-error.js";
import { Button } from "../../shared/ui/button.js";
import type { PositionView } from "./models.js";

export function RecoverPositionControl({ position }: { readonly position: PositionView }) {
  const api = useApi();
  const client = useQueryClient();
  const [confirmed, setConfirmed] = useState(false);
  const recovery = useMutation({
    mutationFn: async () =>
      requireData(
        await api.POST("/positions/recover", {
          body: {
            accountId: position.brokerAccountId,
            symbol: position.symbol,
            confirmExternalClose: true,
          },
        }),
      ),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["positions"] }),
        client.invalidateQueries({ queryKey: ["bots"] }),
        client.invalidateQueries({ queryKey: ["activities"] }),
      ]);
    },
  });
  if (position.reconciliation !== "discrepant") return null;
  return (
    <div className="form-stack">
      <p>
        Closed this position at the broker? Recovery verifies the broker is flat and has no
        unresolved orders. Stop affected bots first. Bots remain inactive after recovery.
      </p>
      <label className="check-field">
        <input
          type="checkbox"
          checked={confirmed}
          disabled={recovery.isPending}
          onChange={(event) => setConfirmed(event.currentTarget.checked)}
        />
        <span>
          I manually closed {position.symbol} and want to accept the broker’s flat position.
        </span>
      </label>
      <Button
        disabled={!confirmed || recovery.isPending}
        onClick={() => recovery.mutate()}
        tone="quiet"
      >
        {recovery.isPending ? "Checking broker…" : "Accept broker position / recover"}
      </Button>
      {recovery.error ? (
        <p role="alert" className="form-error">
          {recovery.error.message}
        </p>
      ) : null}
      {recovery.isSuccess ? (
        <p role="status">Position recovered. Start the bot separately when ready.</p>
      ) : null}
    </div>
  );
}
